import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { nodesToPdf } from '../utils/pdf';
import '../styles/Reports.scss';
import SubMinWarning from '../components/SubMinWarning';
import api from '../services/api';
import { chartColors, themeVars } from '../config/theme';

const CHART = chartColors('enterprise_development');

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);
const fmtCurrency = (n) => `R ${Number(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const buildChartData = (indicators, resultMap, bonusSelected) => ({
  labels: indicators.map((ind) =>
    ind.label.length > 35 ? ind.label.slice(0, 35) + '…' : ind.label
  ),
  datasets: [
    {
      label: 'Points Achieved',
      data: indicators.map((ind) =>
        ind.id === 'Graduation'
          ? (bonusSelected ? 1 : 0)
          : (resultMap[ind.id]?.points ?? 0)
      ),
      backgroundColor: CHART.fill,
      borderColor: CHART.border,
      borderWidth: 1,
      borderRadius: 4,
    },
    {
      label: 'Max Points',
      data: indicators.map((ind) => ind.points),
      backgroundColor: CHART.track,
      borderColor: CHART.trackBorder,
      borderWidth: 1,
      borderRadius: 4,
    },
  ],
});

const chartOptions = {
  responsive: true,
  plugins: {
    legend: { position: 'top' },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: { stepSize: 1 },
      title: { display: true, text: 'Points' },
    },
    x: { ticks: { font: { size: 11 } } },
  },
};

const SpendTable = ({ spendSummary, npat, targetLabel }) => (
  <div className="sc-spend-table-outer">
    <h2 className="sc-spend-title">NPAT &amp; Spend Summary</h2>
    <div className="sc-table-wrapper">
      <table className="sc-table sc-spend-table">
        <thead>
          <tr>
            <th>Category</th>
            <th>NPAT Used</th>
            <th>Target ({targetLabel} of NPAT)</th>
            <th>Interest Free Loan Target (70%)</th>
            <th>Actual Spend</th>
            <th>Difference</th>
          </tr>
        </thead>
        <tbody>
          {spendSummary.map((row) => {
            const diff = row.spend_diff;
            return (
              <tr key={row.category} className={diff > 0 ? 'sc-row--achieved' : 'sc-row--partial'}>
                <td>{row.category}</td>
                <td>{npat !== null ? fmtCurrency(npat) : '—'}</td>
                <td>{fmtCurrency(row.target_spend)}</td>
                <td>{fmtCurrency(row.loan_target)}</td>
                <td>{fmtCurrency(row.spend)}</td>
                <td>
                  <span className={diff > 0 ? 'sc-spend-diff sc-spend-diff--pos' : 'sc-spend-diff sc-spend-diff--neg'}>
                    {diff > 0 ? '+' : ''}{fmtCurrency(diff)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

const SummaryGauge = ({ points, maxPoints }) => {
  const pct = Math.round((points / maxPoints) * 100);
  const offset = 264 - (264 * Math.min(pct, 100) / 100);
  return (
    <div className="reports-summary-card">
      <div className="reports-summary-head">
        <span className="reports-summary-label">Achievement</span>
        <span className={`reports-summary-risk${pct >= 60 ? ' reports-summary-risk--ok' : ''}`}>
          <i></i>{pct >= 60 ? 'On Track' : 'At Risk'}
        </span>
      </div>
      <div className="reports-summary-gauge">
        <svg viewBox="0 0 200 110" preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="gaugeGradESD" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={CHART.fill} />
              <stop offset="100%" stopColor={CHART.fill} />
            </linearGradient>
          </defs>
          <path className="gauge-track" d="M 16 100 A 84 84 0 0 1 184 100" />
          <path className="gauge-fill" d="M 16 100 A 84 84 0 0 1 184 100"
            stroke="url(#gaugeGradESD)"
            strokeDasharray="264"
            strokeDashoffset={offset} />
        </svg>
        <div className="reports-summary-gauge-label">
          <span className="v">{pct}<span className="pct">%</span></span>
          <small>vs 100% full score</small>
        </div>
      </div>
    </div>
  );
};

const ESDReportsChart = () => {
  const navigate = useNavigate();
  const reportRef = useRef(null);

  const [edResults, setEdResults] = useState(null);
  const [edTotal, setEdTotal] = useState(0);
  const [edSpendSummary, setEdSpendSummary] = useState(null);
  const [edBonus, setEdBonus] = useState(false);
  const [edNpat, setEdNpat] = useState(null);
  const [edIndicators, setEdIndicators] = useState([]);

  const [sdResults, setSdResults] = useState(null);
  const [sdTotal, setSdTotal] = useState(0);
  const [sdSpendSummary, setSdSpendSummary] = useState(null);
  const [sdBonus, setSdBonus] = useState(false);
  const [sdNpat, setSdNpat] = useState(null);
  const [sdIndicators, setSdIndicators] = useState([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      api.get('/core/scorecard/get_scorecard/?element=enterprise_development'),
      api.get('/core/scorecard/get_scorecard/?element=supplier_development'),
    ]).then(([edRes, sdRes]) => {
      if (edRes.status === 'fulfilled') {
        const d = edRes.value.data;
        setEdResults(d.results);
        setEdTotal(d.total_points);
        setEdSpendSummary(d.ed_spend_summary ?? null);
        setEdBonus(d.bonus_selected ?? false);
        setEdNpat(d.npat ?? null);
        setEdIndicators(d.data?.indicators || d.indicators || []);
      }
      if (sdRes.status === 'fulfilled') {
        const d = sdRes.value.data;
        setSdResults(d.results);
        setSdTotal(d.total_points);
        setSdSpendSummary(d.sd_spend_summary ?? null);
        setSdBonus(d.bonus_selected ?? false);
        setSdNpat(d.npat ?? null);
        setSdIndicators(d.data?.indicators || d.indicators || []);
      }
      setLoading(false);
    });
  }, []);

  const downloadPDF = async () => {
    const hide = ['.reports-download-btn', '.reports-back'].map((sel) => document.querySelector(sel)).filter(Boolean);
    await nodesToPdf(reportRef.current, 'esd-report.pdf', {
      beforeCapture: () => hide.forEach((n) => { n.style.visibility = 'hidden'; }),
      afterCapture: () => hide.forEach((n) => { n.style.visibility = 'visible'; }),
    });
  };

  const edResultMap = edResults ? Object.fromEntries(edResults.map((r) => [r.id, r])) : {};
  const sdResultMap = sdResults ? Object.fromEntries(sdResults.map((r) => [r.id, r])) : {};
  const edDisplayTotal = edTotal + (edBonus ? 1 : 0);
  const sdDisplayTotal = sdTotal + (sdBonus ? 1 : 0);

  const edMaxPoints = edIndicators.reduce((s, ind) => s + (ind.points || 0), 0);
  const sdMaxPoints = sdIndicators.reduce((s, ind) => s + (ind.points || 0), 0);

  return (
    <div className="reports-container themed" style={themeVars('enterprise_development')} ref={reportRef}>
      <div className="reports-header">
        <button className="reports-back" onClick={() => navigate('/')}>← Back</button>
        <div>
          <h1>Reports</h1>
          <p>B-BBEE — Enterprise &amp; Supplier Development Performance Overview</p>
        </div>
        <button className="reports-back reports-download-btn" style={{ marginLeft: 'auto' }} onClick={downloadPDF}>
          Download PDF
        </button>
      </div>

      <div className="reports-body">
        {loading && <p className="reports-loading">Loading scorecard data…</p>}

        {!loading && !edResults && !sdResults && (
          <div className="reports-empty">
            No scorecard data found. Go to the{' '}
            <button
              className="reports-inline-link"
              onClick={() => navigate('/esd/enterprise-supplier-development/scorecard')}
            >
              ESD Scorecard
            </button>{' '}
            to calculate your results first.
          </div>
        )}

        {/* ── Enterprise Development ── */}
        {!loading && edResults && (
          <>
            <h2 style={{ marginBottom: '0.5rem' }}>Enterprise Development</h2>

            <div className="reports-summary">
              <div className="reports-summary-card reports-summary-card--primary">
                <div className="reports-summary-head">
                  <span className="reports-summary-label">Total Enterprise Development Points</span>
                </div>
                <div className="reports-summary-score">{fmt(edDisplayTotal)} <span>/ {edMaxPoints || '-'}</span></div>
                <div className="reports-summary-ctx">
                  <span>Full score <b>{edMaxPoints || '-'}</b></span>
                  <span>Gap <b>{(edMaxPoints - edDisplayTotal).toFixed(2)}</b></span>
                </div>
              </div>

              <SummaryGauge points={edDisplayTotal} maxPoints={edMaxPoints} />

              <div className="reports-summary-card">
                <div className="reports-summary-head">
                  <span className="reports-summary-label">Target Fully Achieved</span>
                </div>
                <div className="reports-summary-score">
                  {edResults.filter((r) => {
                    const ind = edIndicators.find((i) => i.id === r.id);
                    return ind && r.points >= ind.points;
                  }).length}
                  <span> / {edIndicators.length}</span>
                </div>
              </div>
            </div>

            {edSpendSummary && edSpendSummary.length > 0 && (
              <SpendTable spendSummary={edSpendSummary} npat={edNpat} targetLabel="1%" />
            )}

            <SubMinWarning results={edResults} />

            <div className="reports-chart-card">
              <h2 className="reports-chart-heading">Enterprise Development — Points Achieved vs Maximum</h2>
              <p className="reports-chart-sub">Breakdown of Requirements</p>
              <Bar data={buildChartData(edIndicators, edResultMap, edBonus)} options={chartOptions} />
            </div>
          </>
        )}

        {/* ── Supplier Development ── */}
        {!loading && sdResults && (
          <>
            <h2 style={{ margin: '2rem 0 0.5rem' }}>Supplier Development</h2>

            <div className="reports-summary">
              <div className="reports-summary-card reports-summary-card--primary">
                <div className="reports-summary-head">
                  <span className="reports-summary-label">Total Supplier Development Points</span>
                </div>
                <div className="reports-summary-score">{fmt(sdDisplayTotal)} <span>/ {sdMaxPoints || '-'}</span></div>
                <div className="reports-summary-ctx">
                  <span>Full score <b>/ {sdMaxPoints || '-'}</b></span>
                  <span>Gap <b>{(sdMaxPoints - sdDisplayTotal).toFixed(2)}</b></span>
                </div>
              </div>

              <SummaryGauge points={sdDisplayTotal} maxPoints={sdMaxPoints} />

              <div className="reports-summary-card">
                <div className="reports-summary-head">
                  <span className="reports-summary-label">Target Fully Achieved</span>
                </div>
                <div className="reports-summary-score">
                  {sdResults.filter((r) => {
                    const ind = sdIndicators.find((i) => i.id === r.id);
                    return ind && r.points >= ind.points;
                  }).length}
                  <span> / {sdIndicators.length}</span>
                </div>
              </div>
            </div>

            {sdSpendSummary && sdSpendSummary.length > 0 && (
              <SpendTable spendSummary={sdSpendSummary} npat={sdNpat} targetLabel="2%" />
            )}

            <SubMinWarning results={sdResults} />

            <div className="reports-chart-card">
              <h2 className="reports-chart-heading">Supplier Development — Points Achieved vs Maximum</h2>
              <p className="reports-chart-sub">Breakdown of Requirements</p>
              <Bar data={buildChartData(sdIndicators, sdResultMap, sdBonus)} options={chartOptions} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ESDReportsChart;
